<template>
  <div class="detail-chat-panel">
    <transition name="chat-fab-fade">
      <div
        v-show="!visible"
        class="detail-chat-fab-wrap"
        :class="{
          'detail-chat-fab-wrap--open': fabMenuOpen,
          'detail-chat-fab-wrap--left': fabDockSide === 'left',
          'detail-chat-fab-wrap--dragging': isFabDragging,
          'detail-chat-fab-wrap--snapping': fabSnapping,
          'detail-chat-fab-wrap--guide': fabGuideVisible
        }"
        :style="fabStyle"
      >
        <button
          type="button"
          class="detail-chat-fab-sat detail-chat-fab-sat--expand"
          :class="{
            'detail-chat-fab-sat--guide-pulse': fabGuideStep === 1,
            'detail-chat-fab-sat--guide-dim': fabGuideStep === 2
          }"
          :disabled="expandDisabled || expandLoading"
          :aria-label="expandLoading ? '正在生成知识扩展' : '智能知识扩展'"
          @click.stop="onSmartExpandClick"
        >
          <i v-if="expandLoading" class="el-icon-loading"></i>
          <i v-else class="el-icon-notebook-2"></i>
          <span>{{ expandLoading ? '生成中' : '知识扩展' }}</span>
        </button>
        <button
          type="button"
          class="detail-chat-fab-sat detail-chat-fab-sat--chat"
          :class="{
            'detail-chat-fab-sat--guide-pulse': fabGuideStep === 2,
            'detail-chat-fab-sat--guide-dim': fabGuideStep === 1
          }"
          aria-label="打开 AI 聊天"
          @click.stop="onChatActionClick"
        >
          <i class="el-icon-chat-dot-round"></i>
          <span>AI 聊天</span>
        </button>
        <button
          ref="fab"
          type="button"
          class="detail-chat-fab"
          :class="{
            'detail-chat-fab--open': fabMenuOpen,
            'detail-chat-fab--dragging': isFabDragging
          }"
          :aria-label="fabMenuOpen ? '收起功能菜单' : '打开功能菜单，拖动后松手将吸附到侧边'"
          :aria-expanded="fabMenuOpen ? 'true' : 'false'"
          @mousedown="onFabPointerDown"
          @touchstart="onFabPointerDown"
        >
          <i :class="fabMenuOpen ? 'el-icon-close' : 'el-icon-s-opportunity'"></i>
        </button>
      </div>
    </transition>

    <transition name="fab-guide-fade">
      <div
        v-if="fabGuideVisible && !visible"
        class="detail-fab-guide"
        role="dialog"
        :aria-label="fabGuideStep === 1 ? '知识扩展引导' : 'AI 聊天引导'"
      >
        <div class="detail-fab-guide__mask" @click="onFabGuideNext"></div>
        <div
          class="detail-fab-guide__tip"
          :class="{ 'detail-fab-guide__tip--left': fabDockSide === 'left' }"
          :key="'fab-guide-step-' + fabGuideStep"
          :style="fabGuideTipStyle"
        >
          <p class="detail-fab-guide__step">{{ fabGuideStep }}/2</p>
          <p class="detail-fab-guide__title">{{ fabGuideTitle }}</p>
          <p class="detail-fab-guide__desc" v-html="fabGuideDesc"></p>
          <button type="button" class="detail-fab-guide__btn" @click="onFabGuideNext">
            {{ fabGuideStep >= 2 ? '知道了' : '下一步' }}
          </button>
        </div>
      </div>
    </transition>

    <transition name="chat-fab-menu-mask-fade">
      <div
        v-if="fabMenuOpen && !visible && !fabGuideVisible"
        class="detail-chat-fab-menu-mask"
        aria-hidden="true"
        @click="closeFabMenu"
      ></div>
    </transition>

    <transition name="chat-mask-fade">
      <div
        v-if="visible"
        class="detail-chat-mask"
        aria-hidden="true"
        @click="closePanel"
      ></div>
    </transition>

    <transition
      name="chat-panel-expand"
      @before-enter="onBeforeEnter"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
    >
      <div
        v-if="visible"
        ref="panel"
        class="detail-chat-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="聊天"
        @click.stop
      >
        <header class="detail-chat-sheet__header">
          <div class="detail-chat-sheet__title-wrap">
            <span class="detail-chat-sheet__avatar" aria-hidden="true">
              <i class="el-icon-chat-line-round"></i>
            </span>
            <div>
              <h3 class="detail-chat-sheet__title">智能小助手-Evy</h3>
              <p class="detail-chat-sheet__subtitle">针对当前内容提问</p>
            </div>
          </div>
          <button
            type="button"
            class="detail-chat-sheet__close"
            aria-label="关闭聊天"
            @click="closePanel"
          >
            <i class="el-icon-close"></i>
          </button>
        </header>

        <div
          class="detail-chat-sheet__body"
          ref="chatMessages"
          @scroll.passive="onChatScroll"
        >
          <div v-if="!chatList.length" class="detail-chat-welcome">
            <p class="detail-chat-welcome__title">您好，我是 AI 助手</p>
            <p class="detail-chat-welcome__desc">
              可以就当前总结内容直接提问，或者长按文本选中后快捷提问
            </p>
          </div>
          <div v-if="historyLoading" class="detail-chat-history-loading" aria-label="加载中">
            <i class="el-icon-loading"></i>
          </div>
          <div v-else-if="loadingMore" class="detail-chat-history-loading detail-chat-history-loading--more" aria-label="加载中">
            <i class="el-icon-loading"></i>
          </div>
          <div v-if="chatList.length" class="detail-chat-messages">
            <template v-for="(item, index) in chatList">
              <div
                v-if="item.chatType === 'user'"
                :key="'user-' + (item.id || index)"
                class="detail-chat-row detail-chat-row--user"
              >
                <div class="detail-chat-bubble detail-chat-bubble--user">
                  <p class="detail-chat-bubble__text">{{ item.content }}</p>
                </div>
                <div class="chat-avatar chat-avatar--user">
                  <img v-if="userAvatarUrl" :src="userAvatarUrl" alt="" class="chat-avatar__img" />
                  <span v-else class="chat-avatar__initial">{{ userAvatarInitial }}</span>
                </div>
              </div>
              <div
                v-else
                :key="'bot-' + (item.botMsgId || item.id || index)"
                class="detail-chat-bot-block"
              >
                <div class="detail-chat-row detail-chat-row--bot">
                  <div class="chat-avatar chat-avatar--bot">
                    <i class="el-icon-chat-line-round"></i>
                  </div>
                  <div class="detail-chat-bubble detail-chat-bubble--bot">
                    <div v-if="item.isLoading" class="detail-chat-typing">
                      <span></span><span></span><span></span>
                    </div>
                    <div
                      v-else
                      class="detail-chat-bubble__content"
                      v-html="item.content"
                    ></div>
                  </div>
                </div>
                <button
                  v-if="item.isComplete && item.plainText"
                  type="button"
                  class="detail-chat-add-note"
                  @click="addToNote(item)"
                >
                  <i class="el-icon-document"></i>
                  添加为笔记
                </button>
              </div>
            </template>
          </div>
        </div>

        <footer class="detail-chat-sheet__footer">
          <el-input
            ref="chatInput"
            v-model="draft"
            type="textarea"
            :rows="1"
            :maxlength="chatInputMaxLength"
            :disabled="isSending"
            resize="none"
            placeholder="Enter 发送，Shift+Enter 换行"
            class="detail-chat-input"
            @input="syncChatInputHeight"
            @keydown.native="handleChatKeydown"
          />
          <button
            type="button"
            class="detail-chat-send"
            :disabled="!draft.trim() || isSending"
            @click="onSend"
          >
            <i class="el-icon-s-promotion"></i>
          </button>
        </footer>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import { marked } from 'marked'
import { getAccessToken } from '@/utils/accessToken'
import { getStoredUserProfile, getProfileAvatarPreset } from '@/utils/userSession'
import defaultManImg from '@/assets/default-man.jpg?url'
import defaultWomanImg from '@/assets/default-woman.jpg?url'

const FAB_DRAG_THRESHOLD = 10
const CHAT_INPUT_MAX_LENGTH = 6000

export default {
  name: 'DetailChatPanel',
  props: {
    /** 当前总结详情 ID，用于按「用户 + 总结」隔离聊天记录 */
    summaryId: {
      type: String,
      default: ''
    },
    /** 智能知识扩展是否禁用 */
    expandDisabled: {
      type: Boolean,
      default: false
    },
    /** 智能知识扩展是否生成中 */
    expandLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      fabMenuOpen: false,
      fabGuideStep: 0,
      fabGuideTimer: null,
      draft: '',
      botMsgId: null,
      currentUserName: null,
      conversationId: null,
      chatList: [],
      historyLoading: false,
      historyLoaded: false,
      historyHasMore: false,
      oldestSortNo: null,
      loadingMore: false,
      isSending: false,
      sseSource: null,
      fabCenter: null,
      fabPos: null,
      fabDockSide: 'right',
      fabSize: 56,
      isFabDragging: false,
      fabSnapping: false,
      fabDragState: null,
      fabSnapTimer: null,
      defaultManImg,
      defaultWomanImg,
      avatarPreset: getProfileAvatarPreset()
    }
  },
  computed: {
    fabGuideVisible() {
      return this.fabGuideStep > 0
    },
    fabGuideTitle() {
      return this.fabGuideStep === 1 ? '一键知识扩展' : 'AI 聊天'
    },
    fabGuideDesc() {
      if (this.fabGuideStep === 1) {
        return '基于当前总结，自动整理可复习的<strong>知识点笔记</strong>'
      }
      return '针对这篇内容随时提问，让<strong>智能小助手-Evy</strong>帮你解答'
    },
    fabStyle() {
      if (!this.fabPos) return {}
      return {
        left: `${this.fabPos.x}px`,
        top: `${this.fabPos.y}px`,
        right: 'auto',
        bottom: 'auto'
      }
    },
    fabGuideTipStyle() {
      if (!this.fabPos || !this.fabGuideStep) return {}
      const mobile = this.isMobileViewport()
      const tipWidth = mobile ? 200 : 220
      const tipApproxH = 136
      const gap = 14
      const satHalf = mobile ? 29 : 32
      const offset = this.getFabGuideTargetOffset()
      const fabCx = this.fabPos.x + this.fabSize / 2
      const fabCy = this.fabPos.y + this.fabSize / 2
      const satCx = fabCx + offset.dx
      const satCy = fabCy + offset.dy
      const vh = typeof window !== 'undefined' ? window.innerHeight : 800
      const vw = typeof window !== 'undefined' ? window.innerWidth : 400
      // 知识扩展在上方：气泡偏上，避免盖住下方的「AI 聊天」
      const lift = this.fabGuideStep === 1 ? (mobile ? 44 : 52) : 0
      let top = satCy - tipApproxH / 2 - lift
      top = Math.max(12, Math.min(top, vh - tipApproxH - 12))
      const arrowTop = Math.max(18, Math.min(satCy - top - 6, tipApproxH - 24))
      const style = {
        top: `${top}px`,
        width: `${tipWidth}px`,
        '--fab-guide-arrow-top': `${arrowTop}px`
      }
      if (this.fabDockSide === 'left') {
        style.left = `${Math.min(vw - tipWidth - 12, satCx + satHalf + gap)}px`
      } else {
        style.left = `${Math.max(12, satCx - satHalf - gap - tipWidth)}px`
      }
      return style
    },
    chatInputMaxLength() {
      return CHAT_INPUT_MAX_LENGTH
    },
    userAvatarUrl() {
      const profile = getStoredUserProfile()
      const serverAvatar = profile.avatar ? String(profile.avatar).trim() : ''
      if (serverAvatar === '/preset-avatar/man') return this.defaultManImg
      if (serverAvatar === '/preset-avatar/woman') return this.defaultWomanImg
      if (serverAvatar && (serverAvatar.startsWith('http') || serverAvatar.startsWith('data:'))) {
        return serverAvatar
      }
      const preset = this.avatarPreset || getProfileAvatarPreset()
      if (preset === 'man') return this.defaultManImg
      if (preset === 'woman') return this.defaultWomanImg
      return ''
    },
    userAvatarInitial() {
      const profile = getStoredUserProfile()
      const name = profile.userName || profile.userId || ''
      return name ? String(name).trim().charAt(0).toUpperCase() : '我'
    }
  },
  watch: {
    summaryId(next, prev) {
      if (next === prev) return
      this.resetHistoryState()
      this.syncConversationId()
      if (this.visible && next) {
        this.loadChatHistory()
      }
    }
  },
  mounted() {
    const userId = this.getUserId()
    if (userId) {
      this.currentUserName = userId
    }
    this.syncConversationId()
    this.$nextTick(() => {
      this.initFabPosition()
      this.syncChatInputHeight()
      this.scheduleFabGuide()
    })
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy() {
    this.closeSSE()
    this.teardownFabDragListeners()
    this.clearFabGuideTimer()
    if (this.fabSnapTimer) {
      window.clearTimeout(this.fabSnapTimer)
      this.fabSnapTimer = null
    }
    window.removeEventListener('resize', this.onWindowResize)
    this.unlockBodyScroll()
  },
  methods: {
    getUserId() {
      return localStorage.getItem('userId') || null
    },

    clearFabGuideTimer() {
      if (this.fabGuideTimer) {
        window.clearTimeout(this.fabGuideTimer)
        this.fabGuideTimer = null
      }
    },

    /** 与卫星按钮 CSS 位移一致，引导气泡对准当前功能块 */
    getFabGuideTargetOffset() {
      const mobile = this.isMobileViewport()
      const sign = this.fabDockSide === 'left' ? 1 : -1
      if (this.fabGuideStep === 1) {
        return { dx: sign * (mobile ? 46 : 52), dy: mobile ? -72 : -78 }
      }
      return { dx: sign * (mobile ? 80 : 88), dy: mobile ? 24 : 28 }
    },

    scheduleFabGuide() {
      this.clearFabGuideTimer()
      this.fabGuideTimer = window.setTimeout(() => {
        this.fabGuideTimer = null
        if (this.visible) return
        this.fabGuideStep = 1
        this.fabMenuOpen = true
      }, 700)
    },

    onFabGuideNext() {
      if (this.fabGuideStep < 2) {
        this.fabGuideStep = 2
        return
      }
      this.dismissFabGuide()
    },

    dismissFabGuide() {
      if (!this.fabGuideVisible) return
      this.fabGuideStep = 0
      this.fabMenuOpen = false
      this.clearFabGuideTimer()
    },

    ensureSSEConnect() {
      if (this.sseSource) return true
      const userId = this.getUserId()
      if (!userId) {
        this.$message.error('请先登录')
        return false
      }
      this.currentUserName = userId
      this.initSSE(userId)
      return true
    },

    initSSE(userId) {
      if (this.sseSource) return
      if (!window.EventSource) {
        this.$message.error('浏览器不支持 SSE')
        return
      }
      const token = getAccessToken()
      if (!token) {
        this.$message.error('请先登录后再使用 AI 聊天')
        return
      }
      const url = `/api/app/sse/connect?userId=${encodeURIComponent(userId)}&access_token=${encodeURIComponent(token)}`
      this.sseSource = new EventSource(url)

      this.sseSource.addEventListener('add', (e) => {
        this.handleSSEAdd(e.data)
      })

      this.sseSource.addEventListener('finish', (e) => {
        this.handleSSEFinish(e.data)
      })

      this.sseSource.onerror = () => {
        if (this.sseSource && this.sseSource.readyState === EventSource.CLOSED) {
          this.closeSSE()
        }
      }
    },

    closeSSE() {
      if (this.sseSource) {
        this.sseSource.close()
        this.sseSource = null
      }
    },

    handleSSEAdd(data) {
      const botMsgId = this.botMsgId
      if (!botMsgId) return

      const chatItem = this.chatList.find((item) => item.botMsgId === botMsgId)
      if (!chatItem) return

      if (chatItem.isLoading) {
        chatItem.isLoading = false
      }
      chatItem.content = (chatItem.content || '') + data
      this.scrollToBottom()
    },

    handleSSEFinish(data) {
      let chatResponse
      try {
        chatResponse = JSON.parse(data)
      } catch (e) {
        console.error('finish 事件解析失败:', e)
        return
      }

      const message = chatResponse.message
      const botMsgId = chatResponse.botMsgId
      const chatItem = this.chatList.find((item) => item.botMsgId === botMsgId)

      if (chatItem) {
        chatItem.plainText = message || ''
        chatItem.content = marked.parse(message || '')
        chatItem.isLoading = false
        chatItem.isComplete = true
      } else {
        this.chatList.push({
          content: marked.parse(message || ''),
          plainText: message || '',
          userName: 'bot',
          chatType: 'bot',
          botMsgId,
          isComplete: true
        })
      }

      this.botMsgId = null
      this.isSending = false
      this.scrollToBottom()
    },

    generateMsgId() {
      return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    },

    syncConversationId() {
      const userId = this.currentUserName || this.getUserId()
      const summaryId = this.summaryId ? String(this.summaryId).trim() : ''
      if (userId && summaryId) {
        this.conversationId = `detail:${userId}:${summaryId}`
        return
      }
      this.conversationId = `conv_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    },

    resetHistoryState() {
      this.historyLoaded = false
      this.historyHasMore = false
      this.oldestSortNo = null
      this.loadingMore = false
      this.chatList = []
    },

    currentSummaryId() {
      return this.summaryId ? String(this.summaryId).trim() : ''
    },

    mapHistoryItem(item) {
      const chatType = item.chatType === 'bot' || item.chatType === 'assistant' ? 'bot' : 'user'
      const plain = item.content != null ? String(item.content) : ''
      const sortNo = item.sortNo != null && item.sortNo !== '' ? String(item.sortNo) : null
      if (chatType === 'bot') {
        return {
          id: item.id,
          content: marked.parse(plain || ''),
          plainText: plain,
          userName: 'bot',
          chatType: 'bot',
          botMsgId: item.botMsgId || item.id,
          sortNo,
          isComplete: true,
          isLoading: false
        }
      }
      return {
        id: item.id,
        content: plain,
        userName: this.currentUserName,
        chatType: 'user',
        sortNo
      }
    },

    syncOldestSortNo() {
      const first = this.chatList[0]
      this.oldestSortNo = first && first.sortNo != null ? first.sortNo : null
    },

    async loadChatHistory() {
      const summaryId = this.currentSummaryId()
      if (!summaryId || this.historyLoading) return

      const requestedId = summaryId
      this.historyLoading = true
      try {
        const { data } = await axios.get('/api/app/chat/history', {
          params: { summaryId, limit: 100 }
        })
        if (requestedId !== this.currentSummaryId()) return

        const records = data && Array.isArray(data.records) ? data.records : []
        this.chatList = records.map((item) => this.mapHistoryItem(item))
        this.historyHasMore = !!(data && data.hasMore)
        this.syncOldestSortNo()
        this.historyLoaded = true
        this.$nextTick(() => this.scrollToBottom())
      } catch (error) {
        console.error('加载聊天记录失败:', error)
        if (requestedId === this.currentSummaryId()) {
          this.$message.error('加载聊天记录失败，请稍后重试')
        }
      } finally {
        this.historyLoading = false
      }
    },

    async loadMoreHistory() {
      const summaryId = this.currentSummaryId()
      if (!summaryId || !this.historyHasMore || this.loadingMore || this.historyLoading) return
      if (this.oldestSortNo == null) return

      const requestedId = summaryId
      const beforeSortNo = this.oldestSortNo
      const body = this.$refs.chatMessages
      const prevHeight = body ? body.scrollHeight : 0
      const prevTop = body ? body.scrollTop : 0

      this.loadingMore = true
      try {
        const { data } = await axios.get('/api/app/chat/history', {
          params: { summaryId, limit: 100, beforeSortNo }
        })
        if (requestedId !== this.currentSummaryId()) return

        const records = data && Array.isArray(data.records) ? data.records : []
        const mapped = records.map((item) => this.mapHistoryItem(item))
        this.chatList = mapped.concat(this.chatList)
        this.historyHasMore = !!(data && data.hasMore)
        this.syncOldestSortNo()
        this.$nextTick(() => {
          const el = this.$refs.chatMessages
          if (el) {
            el.scrollTop = el.scrollHeight - prevHeight + prevTop
          }
        })
      } catch (error) {
        console.error('加载更多聊天记录失败:', error)
      } finally {
        this.loadingMore = false
      }
    },

    onChatScroll() {
      if (!this.visible || this.historyLoading || this.loadingMore || !this.historyHasMore) return
      const body = this.$refs.chatMessages
      if (!body || body.scrollTop > 40) return
      this.loadMoreHistory()
    },

    addToNote(item) {
      const text = (item.plainText || '').trim()
      if (!text) return
      this.$emit('append-note', text)
      this.closePanel()
    },

    handleChatKeydown(event) {
      if (event.isComposing || event.keyCode === 229) {
        return
      }

      const isEnter = event.key === 'Enter' || event.keyCode === 13
      if (!isEnter) {
        return
      }

      // Shift+Enter 保留默认换行行为
      if (event.shiftKey) {
        return
      }

      // Ctrl/Cmd+Enter 手动插入换行
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
        const textarea = this.$refs.chatInput && this.$refs.chatInput.$refs.textarea
        if (!textarea) {
          return
        }
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const text = this.draft
        this.draft = text.substring(0, start) + '\n' + text.substring(end)
        this.$nextTick(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 1
          textarea.focus()
          this.syncChatInputHeight()
        })
        return
      }

      event.preventDefault()
      this.onSend()
    },

    onSend() {
      const pendingMsg = this.draft.trim()
      if (!pendingMsg || this.isSending) return
      if (!this.ensureSSEConnect()) return
      if (this.draft.length > CHAT_INPUT_MAX_LENGTH) {
        this.$message.warning(`消息不能超过 ${CHAT_INPUT_MAX_LENGTH} 个字符`)
        this.draft = this.draft.slice(0, CHAT_INPUT_MAX_LENGTH)
        this.syncChatInputHeight()
        return
      }

      const botMsgId = this.generateMsgId()
      this.botMsgId = botMsgId
      this.isSending = true

      this.chatList.push({
        content: pendingMsg,
        userName: this.currentUserName,
        chatType: 'user'
      })

      this.chatList.push({
        id: `loading-${botMsgId}`,
        content: '',
        userName: 'bot',
        chatType: 'bot',
        botMsgId,
        isLoading: true
      })

      this.draft = ''
      this.syncChatInputHeight()
      this.scrollToBottom()

      this.sendChatRequest({
        currentUserName: this.currentUserName,
        conversationId: this.conversationId,
        summaryId: this.summaryId ? String(this.summaryId).trim() : '',
        message: pendingMsg,
        botMsgId
      })
    },

    async sendChatRequest(singleChat) {
      try {
        await axios.post('/api/app/chat/doChat', singleChat)
      } catch (error) {
        console.error('[发送消息] 请求失败:', error)
        const chatItem = this.chatList.find((item) => item.botMsgId === singleChat.botMsgId)
        if (chatItem) {
          chatItem.isLoading = false
          chatItem.content = '发送失败，请重试'
        }
        this.botMsgId = null
        this.isSending = false
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const body = this.$refs.chatMessages
        if (body) {
          body.scrollTop = body.scrollHeight
        }
      })
    },

    isMobileViewport() {
      return window.innerWidth <= 768
    },
    getFabEdgeInset() {
      return this.isMobileViewport() ? 14 : 18
    },
    getFabVerticalPadding() {
      const safeTop = 12
      const safeBottom = this.isMobileViewport() ? 20 : 24
      return { top: safeTop, bottom: safeBottom }
    },
    getDefaultFabBottomInset() {
      // 进页默认离底边更高一点，避免贴太靠下
      return this.isMobileViewport() ? 124 : 132
    },
    getDockedX(side) {
      const pad = this.getFabEdgeInset()
      if (side === 'left') return pad
      return window.innerWidth - this.fabSize - pad
    },
    clampFabX(x) {
      const pad = this.getFabEdgeInset()
      const maxX = window.innerWidth - this.fabSize - pad
      return Math.min(Math.max(x, pad), Math.max(pad, maxX))
    },
    clampFabY(y) {
      const { top, bottom } = this.getFabVerticalPadding()
      const maxY = window.innerHeight - this.fabSize - bottom
      return Math.min(Math.max(y, top), Math.max(top, maxY))
    },
    initFabPosition() {
      const fab = this.$refs.fab
      if (!fab) return
      const rect = fab.getBoundingClientRect()
      this.fabSize = rect.width || (this.isMobileViewport() ? 52 : 56)
      if (!this.fabPos) {
        const defaultY = window.innerHeight - this.fabSize - this.getDefaultFabBottomInset()
        this.fabPos = {
          x: this.getDockedX('right'),
          y: this.clampFabY(defaultY)
        }
        this.fabDockSide = 'right'
      } else {
        this.fabPos = {
          x: this.getDockedX(this.fabDockSide),
          y: this.clampFabY(this.fabPos.y)
        }
      }
    },
    onWindowResize() {
      this.$nextTick(() => {
        const fab = this.$refs.fab
        if (fab) {
          this.fabSize = fab.getBoundingClientRect().width || this.fabSize
        }
        if (this.fabPos) {
          this.fabPos = {
            x: this.getDockedX(this.fabDockSide),
            y: this.clampFabY(this.fabPos.y)
          }
        }
      })
    },
    snapFabToSide() {
      if (!this.fabPos) return
      const centerX = this.fabPos.x + this.fabSize / 2
      const side = centerX < window.innerWidth / 2 ? 'left' : 'right'
      this.fabDockSide = side
      this.fabSnapping = true
      this.fabPos = {
        x: this.getDockedX(side),
        y: this.clampFabY(this.fabPos.y)
      }
      if (this.fabSnapTimer) {
        window.clearTimeout(this.fabSnapTimer)
      }
      this.fabSnapTimer = window.setTimeout(() => {
        this.fabSnapping = false
        this.fabSnapTimer = null
      }, 320)
    },
    getPointerClientPoint(e) {
      if (e.touches && e.touches.length) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY }
      }
      if (e.changedTouches && e.changedTouches.length) {
        return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY }
      }
      return { x: e.clientX, y: e.clientY }
    },
    bindFabDragListeners() {
      document.addEventListener('mousemove', this.onFabDragMove)
      document.addEventListener('mouseup', this.onFabDragEnd)
      document.addEventListener('touchmove', this.onFabDragMove, { passive: false })
      document.addEventListener('touchend', this.onFabDragEnd)
      document.addEventListener('touchcancel', this.onFabDragEnd)
    },
    teardownFabDragListeners() {
      document.removeEventListener('mousemove', this.onFabDragMove)
      document.removeEventListener('mouseup', this.onFabDragEnd)
      document.removeEventListener('touchmove', this.onFabDragMove)
      document.removeEventListener('touchend', this.onFabDragEnd)
      document.removeEventListener('touchcancel', this.onFabDragEnd)
    },
    onFabPointerDown(e) {
      if (this.visible) return
      if (e.type === 'touchstart' && e.cancelable) {
        e.preventDefault()
      }
      const fab = this.$refs.fab
      if (!fab) return
      const point = this.getPointerClientPoint(e)
      const rect = fab.getBoundingClientRect()
      this.fabSize = rect.width || this.fabSize
      if (!this.fabPos) {
        this.fabPos = {
          x: this.clampFabX(rect.left),
          y: this.clampFabY(rect.top)
        }
      }
      this.fabSnapping = false
      this.isFabDragging = false
      this.fabDragState = {
        startX: point.x,
        startY: point.y,
        originX: this.fabPos.x,
        originY: this.fabPos.y,
        moved: false
      }
      this.bindFabDragListeners()
    },
    onFabDragMove(e) {
      if (!this.fabDragState) return
      if (e.type === 'touchmove' && e.cancelable) {
        e.preventDefault()
      }
      const point = this.getPointerClientPoint(e)
      const dx = point.x - this.fabDragState.startX
      const dy = point.y - this.fabDragState.startY
      if (!this.fabDragState.moved && Math.hypot(dx, dy) > FAB_DRAG_THRESHOLD) {
        this.fabDragState.moved = true
        this.isFabDragging = true
        this.closeFabMenu()
      }
      if (!this.fabDragState.moved) return
      this.fabPos = {
        x: this.clampFabX(this.fabDragState.originX + dx),
        y: this.clampFabY(this.fabDragState.originY + dy)
      }
    },
    onFabDragEnd() {
      const dragState = this.fabDragState
      const shouldToggleMenu = dragState && !dragState.moved
      const didDrag = dragState && dragState.moved
      this.fabDragState = null
      this.isFabDragging = false
      this.teardownFabDragListeners()
      if (didDrag) {
        if (this.fabGuideVisible) {
          this.dismissFabGuide()
        }
        this.snapFabToSide()
      } else if (shouldToggleMenu) {
        this.toggleFabMenu()
      }
    },
    toggleFabMenu() {
      if (this.fabGuideVisible) {
        this.onFabGuideNext()
        return
      }
      this.fabMenuOpen = !this.fabMenuOpen
    },
    closeFabMenu() {
      if (this.fabGuideVisible) {
        this.dismissFabGuide()
        return
      }
      this.fabMenuOpen = false
    },
    onChatActionClick() {
      this.closeFabMenu()
      this.openPanel()
    },
    onSmartExpandClick() {
      if (this.expandDisabled || this.expandLoading) return
      this.closeFabMenu()
      this.$emit('smart-expand')
    },
    getFabCenter() {
      const fab = this.$refs.fab
      if (!fab) return null
      const rect = fab.getBoundingClientRect()
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      }
    },
    applyPanelOrigin(panelEl, center) {
      if (!center || !panelEl) return
      const rect = panelEl.getBoundingClientRect()
      panelEl.style.transformOrigin = `${center.x - rect.left}px ${center.y - rect.top}px`
    },
    lockBodyScroll() {
      document.body.style.overflow = 'hidden'
    },
    unlockBodyScroll() {
      document.body.style.overflow = ''
    },
    openPanel() {
      this.dismissFabGuide()
      this.closeFabMenu()
      this.fabCenter = this.getFabCenter()
      this.visible = true
      this.lockBodyScroll()
      this.ensureSSEConnect()
      this.syncConversationId()
      if (!this.historyLoaded) {
        this.loadChatHistory()
      }
      this.$nextTick(() => {
        this.scrollToBottom()
        this.syncChatInputHeight()
      })
    },
    openWithSelectedText(selectedText) {
      const quote = String(selectedText || '').trim()
      if (!quote) return
      const snippet = quote.length > 300 ? `${quote.slice(0, 300)}…` : quote
      const prefix = '请帮我解释一下下面这段内容：\n'
      const body = `「${snippet}」`
      let draft = `${prefix}${body}`
      if (draft.length > CHAT_INPUT_MAX_LENGTH) {
        const maxSnippetLen = Math.max(0, CHAT_INPUT_MAX_LENGTH - prefix.length - 2)
        draft = `${prefix}「${snippet.slice(0, maxSnippetLen)}」`
      }
      this.draft = draft
      this.openPanel()
      this.$nextTick(() => {
        this.syncChatInputHeight()
        const input = this.$refs.chatInput
        if (input && input.focus) {
          input.focus()
        }
      })
    },
    closePanel() {
      this.visible = false
      this.unlockBodyScroll()
    },
    onBeforeEnter(el) {
      this.applyPanelOrigin(el, this.fabCenter)
      el.style.transform = 'scale(0.12)'
      el.style.opacity = '0'
    },
    onEnter(el, done) {
      this.applyPanelOrigin(el, this.fabCenter)
      el.style.transition =
        'transform 0.38s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s ease'
      requestAnimationFrame(() => {
        el.style.transform = 'scale(1)'
        el.style.opacity = '1'
      })
      const onEnd = (e) => {
        if (e.target !== el || e.propertyName !== 'transform') return
        el.removeEventListener('transitionend', onEnd)
        done()
      }
      el.addEventListener('transitionend', onEnd)
    },
    onAfterEnter(el) {
      el.style.transition = ''
      el.style.transform = ''
      el.style.opacity = ''
      el.style.transformOrigin = ''
    },
    onLeave(el, done) {
      this.applyPanelOrigin(el, this.fabCenter)
      el.style.transition =
        'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.24s ease'
      requestAnimationFrame(() => {
        el.style.transform = 'scale(0.12)'
        el.style.opacity = '0'
      })
      const onEnd = (e) => {
        if (e.target !== el || e.propertyName !== 'transform') return
        el.removeEventListener('transitionend', onEnd)
        done()
      }
      el.addEventListener('transitionend', onEnd)
    },
    syncChatInputHeight() {
      this.$nextTick(() => {
        const input = this.$refs.chatInput
        const textarea = input && input.$refs && input.$refs.textarea
        if (!textarea) return
        const footer = this.$el && this.$el.querySelector('.detail-chat-sheet__footer')
        if (!footer) return
        const style = window.getComputedStyle(footer)
        const controlH = parseFloat(style.getPropertyValue('--chat-control-h')) || 44
        const lineH = parseFloat(style.getPropertyValue('--chat-line-h')) || 20
        const maxH = controlH + lineH
        textarea.style.height = `${controlH}px`
        textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, controlH), maxH)}px`
      })
    }
  }
}
</script>

<style scoped>
.detail-chat-panel {
  position: relative;
  z-index: 0;
}

.detail-chat-fab-menu-mask {
  position: fixed;
  inset: 0;
  z-index: 115;
  background: rgba(10, 22, 48, 0.18);
}

.detail-fab-guide {
  position: fixed;
  inset: 0;
  z-index: 118;
  pointer-events: none;
}

.detail-fab-guide__mask {
  position: absolute;
  inset: 0;
  background: rgba(8, 16, 36, 0.42);
  pointer-events: auto;
}

.detail-fab-guide__tip {
  position: absolute;
  z-index: 2;
  pointer-events: auto;
  padding: 14px 14px 12px;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(16, 28, 58, 0.22);
  box-sizing: border-box;
  animation: fab-guide-tip-in 0.38s cubic-bezier(0.22, 1, 0.36, 1);
}

.detail-fab-guide__tip::after {
  content: '';
  position: absolute;
  top: var(--fab-guide-arrow-top, 36px);
  right: -6px;
  width: 12px;
  height: 12px;
  background: #fff;
  transform: rotate(45deg);
  box-shadow: 2px -2px 6px rgba(16, 28, 58, 0.06);
}

.detail-fab-guide__tip--left::after {
  right: auto;
  left: -6px;
  box-shadow: -2px 2px 6px rgba(16, 28, 58, 0.06);
}

.detail-fab-guide__step {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #8a9bb8;
}

.detail-fab-guide__title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: #16294f;
}

.detail-fab-guide__desc {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.55;
  color: #5b6b88;
}

.detail-fab-guide__desc >>> strong {
  color: #2b6cff;
  font-weight: 700;
}

.detail-fab-guide__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.fab-guide-fade-enter-active,
.fab-guide-fade-leave-active {
  transition: opacity 0.25s ease;
}

.fab-guide-fade-enter,
.fab-guide-fade-leave-to {
  opacity: 0;
}

@keyframes fab-guide-tip-in {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes fab-guide-sat-bounce {
  0%,
  100% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.08);
  }
}

.detail-chat-fab-sat--guide-pulse {
  animation: fab-guide-sat-bounce 1.2s ease-in-out infinite;
  z-index: 4;
  box-shadow: 0 10px 26px rgba(66, 83, 255, 0.28);
}

.detail-chat-fab-sat--guide-dim {
  opacity: 0.4;
  filter: grayscale(0.25);
}

.detail-chat-fab-wrap--guide {
  z-index: 130;
}

.detail-chat-fab-wrap {
  position: fixed;
  right: max(18px, env(safe-area-inset-right, 0px));
  bottom: calc(132px + env(safe-area-inset-bottom, 0px));
  z-index: 120;
  width: 56px;
  height: 56px;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.detail-chat-fab-wrap--snapping {
  transition:
    left 0.3s cubic-bezier(0.22, 1, 0.36, 1),
    top 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.detail-chat-fab {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 3;
  width: 56px;
  height: 56px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%);
  color: #fff;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(66, 83, 255, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.2s ease, opacity 0.2s ease, transform 0.25s ease;
}

.detail-chat-fab:hover:not(.detail-chat-fab--dragging) {
  box-shadow: 0 12px 28px rgba(66, 83, 255, 0.48);
}

.detail-chat-fab--open {
  transform: rotate(90deg);
  box-shadow: 0 10px 26px rgba(66, 83, 255, 0.5);
}

.detail-chat-fab--dragging {
  cursor: grabbing;
  opacity: 0.92;
  box-shadow: 0 10px 26px rgba(66, 83, 255, 0.5);
}

.detail-chat-fab-sat {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 64px;
  height: 64px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #ffffff;
  color: #3546e8;
  box-shadow: 0 8px 22px rgba(22, 41, 79, 0.18);
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, -50%) scale(0.35);
  transition:
    opacity 0.22s ease,
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.2s ease;
}

.detail-chat-fab-sat i {
  font-size: 20px;
  line-height: 1;
}

.detail-chat-fab-sat span {
  font-size: 10px;
  font-weight: 600;
  line-height: 1.1;
  white-space: nowrap;
}

.detail-chat-fab-sat--expand {
  color: #c45a12;
}

.detail-chat-fab-sat--chat {
  color: #3546e8;
}

.detail-chat-fab-wrap--open .detail-chat-fab-sat {
  opacity: 1;
  pointer-events: auto;
}

/*
 * 环绕展开：贴在圆周上（非同一竖线）
 * 右侧停靠 → 向左上 / 正左弧；左侧停靠对称向右
 */
.detail-chat-fab-wrap--open:not(.detail-chat-fab-wrap--left) .detail-chat-fab-sat--expand {
  /* ~125°：左上方 */
  transform: translate(calc(-50% - 52px), calc(-50% - 78px)) scale(1);
  transition-delay: 0.02s;
}

.detail-chat-fab-wrap--open:not(.detail-chat-fab-wrap--left) .detail-chat-fab-sat--chat {
  /* ~195°：左偏下，靠近水平左侧 */
  transform: translate(calc(-50% - 88px), calc(-50% + 28px)) scale(1);
  transition-delay: 0.06s;
}

.detail-chat-fab-wrap--open.detail-chat-fab-wrap--left .detail-chat-fab-sat--expand {
  /* ~55°：右上方 */
  transform: translate(calc(-50% + 52px), calc(-50% - 78px)) scale(1);
  transition-delay: 0.02s;
}

.detail-chat-fab-wrap--open.detail-chat-fab-wrap--left .detail-chat-fab-sat--chat {
  /* ~-15°：右偏下，靠近水平右侧 */
  transform: translate(calc(-50% + 88px), calc(-50% + 28px)) scale(1);
  transition-delay: 0.06s;
}

.detail-chat-fab-sat:hover:not(:disabled) {
  box-shadow: 0 10px 26px rgba(22, 41, 79, 0.24);
}

.detail-chat-fab-wrap--open:not(.detail-chat-fab-wrap--left) .detail-chat-fab-sat--expand:active:not(:disabled) {
  transform: translate(calc(-50% - 52px), calc(-50% - 78px)) scale(0.92);
}

.detail-chat-fab-wrap--open:not(.detail-chat-fab-wrap--left) .detail-chat-fab-sat--chat:active:not(:disabled) {
  transform: translate(calc(-50% - 88px), calc(-50% + 28px)) scale(0.92);
}

.detail-chat-fab-wrap--open.detail-chat-fab-wrap--left .detail-chat-fab-sat--expand:active:not(:disabled) {
  transform: translate(calc(-50% + 52px), calc(-50% - 78px)) scale(0.92);
}

.detail-chat-fab-wrap--open.detail-chat-fab-wrap--left .detail-chat-fab-sat--chat:active:not(:disabled) {
  transform: translate(calc(-50% + 88px), calc(-50% + 28px)) scale(0.92);
}

.detail-chat-fab-sat:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.detail-chat-fab-wrap--open .detail-chat-fab-sat:disabled {
  opacity: 0.55;
}

.chat-fab-menu-mask-fade-enter-active,
.chat-fab-menu-mask-fade-leave-active {
  transition: opacity 0.2s ease;
}

.chat-fab-menu-mask-fade-enter,
.chat-fab-menu-mask-fade-leave-to {
  opacity: 0;
}

.detail-chat-mask {
  position: fixed;
  inset: 0;
  z-index: 130;
  background: rgba(10, 28, 68, 0.38);
  backdrop-filter: blur(2px);
}

.detail-chat-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 140;
  height: 75vh;
  height: 75dvh;
  max-height: 75dvh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -8px 40px rgba(10, 28, 68, 0.14);
  overflow: hidden;
  will-change: transform, opacity;
}

.detail-chat-sheet__header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid #f0f2f8;
}

.detail-chat-sheet__title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.detail-chat-sheet__avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(145deg, #eef2ff 0%, #e0e8ff 100%);
  color: #4652f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.detail-chat-sheet__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #16294f;
  line-height: 1.25;
}

.detail-chat-sheet__subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: #97a5c2;
  line-height: 1.3;
}

.detail-chat-sheet__close {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: #f4f6fa;
  color: #5d6f94;
  font-size: 18px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.detail-chat-sheet__close:hover {
  background: #ebeef5;
}

.detail-chat-sheet__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.detail-chat-history-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 0 8px;
  color: #7b8aa5;
  font-size: 18px;
}

.detail-chat-history-loading--more {
  padding: 8px 0 4px;
  font-size: 16px;
}

.detail-chat-welcome {
  margin-bottom: 16px;
  padding: 14px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f6f8ff 0%, #eef2ff 100%);
  border: 1px solid #e4e9ff;
}

.detail-chat-welcome__title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: #16294f;
}

.detail-chat-welcome__desc {
  margin: 0;
  font-size: 13px;
  color: #6b7a94;
  line-height: 1.5;
}

.detail-chat-messages {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.detail-chat-bot-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  max-width: 100%;
}

.detail-chat-add-note {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 40px;
  padding: 0;
  border: none;
  background: transparent;
  color: #4253ff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.detail-chat-add-note:hover {
  color: #3546e8;
}

.detail-chat-add-note i {
  font-size: 14px;
}

.detail-chat-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 100%;
}

.detail-chat-row--user {
  justify-content: flex-end;
}

.detail-chat-row--bot {
  justify-content: flex-start;
}

.chat-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
  overflow: hidden;
}

.chat-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.chat-avatar__initial {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}

.chat-avatar--bot {
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%);
  color: #fff;
  font-size: 16px;
}

.chat-avatar--user {
  background: linear-gradient(135deg, #52c41a 0%, #3fad09 100%);
}

.detail-chat-bubble {
  max-width: calc(88% - 40px);
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.5;
}

.detail-chat-bubble--bot {
  background: #e2e8f2;
  color: #2d3748;
  border: 1px solid #d0d9e6;
  border-bottom-left-radius: 4px;
}

.detail-chat-bubble--user {
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%);
  color: #ffffff;
  border-bottom-right-radius: 4px;
}

.detail-chat-bubble__text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.detail-chat-bubble__content {
  word-break: break-word;
}

.detail-chat-bubble__content >>> p {
  margin: 0 0 8px;
}

.detail-chat-bubble__content >>> p:last-child {
  margin-bottom: 0;
}

.detail-chat-typing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 16px;
}

.detail-chat-typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #97a5c2;
  display: inline-block;
  animation: detail-chat-typing-bounce 1s infinite ease-in-out;
}

.detail-chat-typing span:nth-child(2) {
  animation-delay: 0.2s;
}

.detail-chat-typing span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes detail-chat-typing-bounce {
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

.detail-chat-sheet__footer {
  --chat-control-h: 44px;
  --chat-line-h: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 12px 14px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #f0f2f8;
  background: #fafbfd;
}

.detail-chat-input {
  flex: 1;
  min-width: 0;
}

.detail-chat-input >>> .el-textarea {
  padding: 0;
}

.detail-chat-input >>> .el-textarea__inner {
  height: var(--chat-control-h) !important;
  min-height: var(--chat-control-h) !important;
  max-height: calc(var(--chat-control-h) + var(--chat-line-h)) !important;
  border-radius: 12px;
  border-color: #e4e7ed;
  font-size: 14px;
  color: #16294f;
  padding: 11px 12px;
  line-height: var(--chat-line-h);
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  word-break: break-word;
}

.detail-chat-input >>> .el-textarea__inner:focus {
  border-color: #4253ff;
}

.detail-chat-send {
  flex-shrink: 0;
  width: var(--chat-control-h);
  height: var(--chat-control-h);
  padding: 0;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%);
  color: #fff;
  font-size: 20px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
  box-sizing: border-box;
}

.detail-chat-send:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.detail-chat-send:not(:disabled):hover {
  transform: translateY(-1px);
}

.chat-fab-fade-enter-active,
.chat-fab-fade-leave-active {
  transition: opacity 0.2s ease;
}

.chat-fab-fade-enter,
.chat-fab-fade-leave-to {
  opacity: 0;
}

.chat-mask-fade-enter-active,
.chat-mask-fade-leave-active {
  transition: opacity 0.28s ease;
}

.chat-mask-fade-enter,
.chat-mask-fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .detail-chat-fab-wrap {
    width: 52px;
    height: 52px;
    right: max(14px, env(safe-area-inset-right, 0px));
    bottom: calc(124px + env(safe-area-inset-bottom, 0px));
  }

  .detail-chat-fab {
    width: 52px;
    height: 52px;
    font-size: 24px;
  }

  .detail-chat-fab-sat {
    width: 58px;
    height: 58px;
  }

  .detail-chat-fab-sat i {
    font-size: 18px;
  }

  .detail-chat-fab-sat span {
    font-size: 9px;
  }

  .detail-chat-fab-wrap--open:not(.detail-chat-fab-wrap--left) .detail-chat-fab-sat--expand {
    transform: translate(calc(-50% - 46px), calc(-50% - 72px)) scale(1);
  }

  .detail-chat-fab-wrap--open:not(.detail-chat-fab-wrap--left) .detail-chat-fab-sat--chat {
    transform: translate(calc(-50% - 80px), calc(-50% + 24px)) scale(1);
  }

  .detail-chat-fab-wrap--open.detail-chat-fab-wrap--left .detail-chat-fab-sat--expand {
    transform: translate(calc(-50% + 46px), calc(-50% - 72px)) scale(1);
  }

  .detail-chat-fab-wrap--open.detail-chat-fab-wrap--left .detail-chat-fab-sat--chat {
    transform: translate(calc(-50% + 80px), calc(-50% + 24px)) scale(1);
  }

  .detail-chat-sheet {
    border-radius: 18px 18px 0 0;
  }

  .detail-chat-sheet__header {
    padding: 12px 14px 10px;
  }

  .detail-chat-sheet__body {
    padding: 14px;
  }

  .detail-chat-sheet__footer {
    --chat-control-h: 42px;
    --chat-line-h: 19px;
    padding: 10px 12px;
    padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  }

  .detail-chat-input >>> .el-textarea__inner {
    padding: 10px 12px;
  }

  .detail-chat-send {
    font-size: 18px;
  }
}

@media (min-width: 769px) {
  .detail-chat-fab {
    right: max(24px, env(safe-area-inset-right, 0px));
  }

  .detail-chat-sheet {
    left: 50%;
    right: auto;
    width: min(520px, calc(100vw - 48px));
    margin-left: calc(min(520px, calc(100vw - 48px)) / -2);
    height: min(75vh, 680px);
    max-height: min(75vh, 680px);
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -12px 48px rgba(10, 28, 68, 0.16);
  }

  .detail-chat-sheet__header {
    padding: 16px 20px 14px;
  }

  .detail-chat-sheet__body {
    padding: 20px;
  }

  .detail-chat-sheet__footer {
    --chat-control-h: 46px;
    --chat-line-h: 21px;
    padding: 14px 20px 18px;
  }

  .detail-chat-input >>> .el-textarea__inner {
    padding: 12px 12px;
  }

  .detail-chat-bubble {
    max-width: calc(82% - 40px);
  }
}
</style>
